import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, eventsApi, type EventWork } from '../services/api'
import { isAuthed, signIn } from '../services/auth'
import LivePreview from './LivePreview'

const NAME_KEY = 'od-event-author-name'
const EMAIL_KEY = 'od-event-author-email'
// Work the visitor clicked upvote on before signing in — replayed once they
// land back here authenticated (see the auto-vote effect below).
const PENDING_VOTE_KEY = 'od-pending-vote'

function UpvoteIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 5v14M12 5l-6 6M12 5l6 6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
      />
    </svg>
  )
}

// Best-effort host extraction for the link preview. Falls back to the raw link.
function hostOf(link: string): string {
  try {
    return new URL(link).host.replace(/^www\./, '')
  } catch {
    return link
  }
}

// Sort by upvotes desc, then oldest-first — same order the API returns, kept in
// sync client-side so the ranking (and the #1 badge) update live after a vote.
function rank(works: EventWork[]): EventWork[] {
  return [...works].sort((a, b) => {
    if (b.upvotes !== a.upvotes) return b.upvotes - a.upvotes
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  })
}

export default function EventWorks({
  slug,
  submissionsEnabled,
}: {
  slug: string
  submissionsEnabled: boolean
}) {
  const [works, setWorks] = useState<EventWork[]>([])
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [workName, setWorkName] = useState('')
  const [link, setLink] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const voting = useRef<Set<string>>(new Set())

  useEffect(() => {
    try {
      const savedName = localStorage.getItem(NAME_KEY)
      if (savedName) setName(savedName)
      const savedEmail = localStorage.getItem(EMAIL_KEY)
      if (savedEmail) setEmail(savedEmail)
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    let active = true
    setLoading(true)
    eventsApi
      .listWorks(slug)
      .then((res) => {
        if (active) setWorks(res.data || [])
      })
      .catch(() => {
        /* leave empty; non-fatal */
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [slug])

  const ranked = useMemo(() => rank(works), [works])
  const autoVoted = useRef(false)

  // Send an unauthenticated visitor through SSO, remembering which work they
  // wanted to upvote so we can finish the vote when they return.
  function promptSignInToVote(workId: string) {
    try {
      sessionStorage.setItem(PENDING_VOTE_KEY, workId)
    } catch {
      /* ignore */
    }
    signIn()
  }

  async function toggleVote(work: EventWork) {
    if (!isAuthed()) {
      promptSignInToVote(work.id)
      return
    }
    if (voting.current.has(work.id)) return
    voting.current.add(work.id)

    const nextVoted = !work.has_voted
    setWorks((prev) =>
      prev.map((w) =>
        w.id === work.id
          ? { ...w, has_voted: nextVoted, upvotes: w.upvotes + (nextVoted ? 1 : -1) }
          : w,
      ),
    )

    try {
      const res = nextVoted
        ? await eventsApi.upvoteWork(work.id)
        : await eventsApi.removeUpvoteWork(work.id)
      setWorks((prev) =>
        prev.map((w) =>
          w.id === work.id ? { ...w, has_voted: res.has_voted, upvotes: res.upvotes } : w,
        ),
      )
    } catch (err) {
      // Roll back the optimistic update.
      setWorks((prev) =>
        prev.map((w) =>
          w.id === work.id ? { ...w, has_voted: work.has_voted, upvotes: work.upvotes } : w,
        ),
      )
      // Stale/expired session — re-authenticate and resume the vote.
      if (err instanceof ApiError && err.status === 401) {
        voting.current.delete(work.id)
        promptSignInToVote(work.id)
        return
      }
    } finally {
      voting.current.delete(work.id)
    }
  }

  // After returning from SSO, replay the upvote the visitor intended.
  useEffect(() => {
    if (loading || autoVoted.current || !isAuthed()) return
    let pending: string | null = null
    try {
      pending = sessionStorage.getItem(PENDING_VOTE_KEY)
    } catch {
      /* ignore */
    }
    if (!pending) return
    autoVoted.current = true
    try {
      sessionStorage.removeItem(PENDING_VOTE_KEY)
    } catch {
      /* ignore */
    }
    const target = works.find((w) => w.id === pending)
    if (target && !target.has_voted) void toggleVote(target)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, works])

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setFormError('')
    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    const trimmedWork = workName.trim()
    const trimmedDescription = description.trim()
    let trimmedLink = link.trim()
    if (!trimmedWork || !trimmedLink || !trimmedName || !trimmedEmail) {
      setFormError('Work name, link, your name and email are all required.')
      return
    }
    if (!/^https?:\/\//i.test(trimmedLink)) {
      trimmedLink = `https://${trimmedLink}`
    }

    setSubmitting(true)
    try {
      const res = await eventsApi.submitWork(slug, {
        name: trimmedWork,
        link: trimmedLink,
        description: trimmedDescription,
        author_name: trimmedName,
        email: trimmedEmail,
      })
      setWorks((prev) => [...prev, res.data])
      setWorkName('')
      setLink('')
      setDescription('')
      try {
        localStorage.setItem(NAME_KEY, trimmedName)
        localStorage.setItem(EMAIL_KEY, trimmedEmail)
      } catch {
        /* ignore */
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not submit your work.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="od-event-works" aria-label="Submitted works">
      <div className="od-event-works-head">
        <h2>
          Showcase
          {works.length > 0 && <span className="od-event-works-count">{works.length}</span>}
        </h2>
        <p className="od-event-works-sub">
          Submit your website and get the chance to win a Stanley coffee cup.
        </p>
      </div>

      {submissionsEnabled ? (
        <form className="od-event-submit" onSubmit={handleSubmit}>
          <div className="od-event-submit-grid">
            <input
              className="od-event-input"
              value={workName}
              onChange={(e) => setWorkName(e.target.value.slice(0, 200))}
              placeholder="Work name"
              aria-label="Work name"
            />
            <input
              className="od-event-input"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="Link (https://…)"
              aria-label="Work link"
            />
            <input
              className="od-event-input"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 120))}
              placeholder="Your name"
              aria-label="Your name"
            />
            <input
              className="od-event-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email (private)"
              aria-label="Your email"
            />
          </div>
          <textarea
            className="od-event-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 1000))}
            placeholder="Description (optional) — tell us what you built"
            aria-label="Work description"
            rows={3}
          />
          <div className="od-event-submit-actions">
            <span className="od-event-submit-hint">Your email is kept private and never shown publicly.</span>
            <button type="submit" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit work'}
            </button>
          </div>
          {formError && <p className="od-event-submit-error">{formError}</p>}
        </form>
      ) : (
        <p className="od-event-works-closed">Submissions are closed for this event.</p>
      )}

      {loading ? (
        <p className="od-event-state">Loading submissions…</p>
      ) : ranked.length === 0 ? (
        <p className="od-event-works-empty">
          {submissionsEnabled ? 'No submissions yet — be the first!' : 'No submissions yet.'}
        </p>
      ) : (
        <ol className="od-event-works-list">
          {ranked.map((work, index) => {
            const isLeader = index === 0 && work.upvotes > 0
            return (
              <li key={work.id} className={`od-event-work${isLeader ? ' is-leader' : ''}`}>
                <span className="od-event-work-rank" aria-hidden="true">
                  {index + 1}
                </span>
                <a
                  className="od-event-work-link"
                  href={work.link}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  <span className="od-event-work-shot">
                    <LivePreview url={work.link} title={work.name} />
                  </span>
                  <span className="od-event-work-main">
                    <span className="od-event-work-title">
                      {work.name}
                      {isLeader && <span className="od-event-work-badge">🏆 Top pick</span>}
                    </span>
                    <span className="od-event-work-meta">
                      <span className="od-event-work-author">by {work.author_name}</span>
                      <span aria-hidden="true">·</span>
                      <span className="od-event-work-host">{hostOf(work.link)}</span>
                    </span>
                    {work.description && (
                      <span className="od-event-work-desc">{work.description}</span>
                    )}
                  </span>
                </a>
                <button
                  type="button"
                  className={`od-event-upvote${work.has_voted ? ' is-voted' : ''}`}
                  onClick={() => toggleVote(work)}
                  aria-pressed={work.has_voted}
                  aria-label={work.has_voted ? 'Remove upvote' : 'Upvote work'}
                >
                  <UpvoteIcon />
                  <span>{work.upvotes}</span>
                </button>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
